import React from "react";
import {
  StyledStatCard,
  StyledStatCardContent,
  StyledStatCardIcon,
  StyledStatCardItem,
  StyledStatCardLabel,
} from "./StatCard.styled";
import { BodyTyp, TitlesTyp } from "../Typography/Typography";
import { theme } from "../../constants/StyledComponents";
import { FontWeightEnum, IconSizeEnum } from "../../types/AppTypes";
import { Tooltip } from "../core/Tooltip/Tooltip";
import { IconManager } from "../core/IconManager/IconManager";
import { ReactComponent as InfoIcon } from "../../icons/info.svg";

export type StatCardItem = {
  icon: React.ReactNode;
  value: string | number;
  label: string | React.ReactNode;
  tooltip?: string;
  error?: boolean;
};

type StatCardProps = {
  items: StatCardItem[];
};

const renderLabel = (label: StatCardItem["label"]) => {
  if (typeof label === "string") {
    return (
      <BodyTyp
        variant="2"
        fontWeight={FontWeightEnum.REGULAR}
        color={theme.colors.grey_1}
      >
        {label}
      </BodyTyp>
    );
  }

  return label;
};

const StatCardItemContent = ({
  icon,
  value,
  label,
  tooltip,
  withDivider,
  error,
}: StatCardItem & { withDivider?: boolean }) => (
  <StyledStatCardItem $withDivider={withDivider}>
    <StyledStatCardIcon>{icon}</StyledStatCardIcon>
    <StyledStatCardContent>
      <TitlesTyp
        variant="3"
        fontWeight={FontWeightEnum.SEMIBOLD}
        color={error ? theme.colors.red_1 : theme.colors.black}
        noTranslate
      >
        {value}
      </TitlesTyp>
      <StyledStatCardLabel>
        {renderLabel(label)}
        {tooltip && (
          <Tooltip message={tooltip}>
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.purple}>
              <InfoIcon />
            </IconManager>
          </Tooltip>
        )}
      </StyledStatCardLabel>
    </StyledStatCardContent>
  </StyledStatCardItem>
);

export const StatCard = ({ items }: StatCardProps) => {
  const multiple = items.length > 1;

  return (
    <StyledStatCard $multiple={multiple}>
      {items.map((item, index) => (
        <StatCardItemContent
          key={`stat-card-${index}`}
          {...item}
          withDivider={multiple && index > 0}
        />
      ))}
    </StyledStatCard>
  );
};
